import { useState, useCallback } from 'react';
import { EmbeddingService } from '../services/embedding-service';
import { DreamAnalyzer } from '../services/dream-analyzer';
import type {
  DreamFormData,
  AnalysisResults,
  RuleMatch,
  EntityMatch,
} from '../types/frontend';

let embeddingService: EmbeddingService | null = null;
let dreamAnalyzer: DreamAnalyzer | null = null;
let isInitialized = false;

// For testing: reset module-level state
export const __resetServiceState = () => {
  embeddingService = null;
  dreamAnalyzer = null;
  isInitialized = false;
};

export const useDreamAnalysis = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const initializeServices = async () => {
    if (isInitialized) return;

    console.log('🚀 Initializing dream analysis services...');

    // Initialize embedding service
    embeddingService = new EmbeddingService();
    await embeddingService.init();

    // Load precomputed embeddings (no data loading needed!)
    await embeddingService.loadEmbeddedRecords();

    // Initialize dream analyzer - pure top-k, no thresholds
    dreamAnalyzer = new DreamAnalyzer(embeddingService);
    dreamAnalyzer.configure({
      maxSentenceResults: 8, // Top-k per sentence
      finalTopK: 8, // Top 8 rules (canonical)
    });

    isInitialized = true;
    console.log('✅ Dream analysis services initialized');
  };

  const analyzeDream = useCallback(async (formData: DreamFormData): Promise<AnalysisResults> => {
    setIsAnalyzing(true);
    const startTime = performance.now();

    try {
      await initializeServices();

      if (!dreamAnalyzer || !embeddingService) {
        throw new Error('Services not initialized');
      }

      console.log('🌙 Analyzing dream:', formData.dreamText.slice(0, 100) + '...');

      // Run dream analysis with context for conditional reranking
      const analysis = await dreamAnalyzer.analyzeDream({
        dream_text: formData.dreamText,
        context: formData.context, // Pass context for conditional rule matching
      });

      // Use segmented search for encyclopedias (symbol-specific matching), whole dream for literature
      const encyclopediaResults = await getSegmentedEncyclopediaMatches(
        formData.dreamText,
        embeddingService
      );

      // Transform entity results (already segmented and merged)
      const entities = encyclopediaResults;

      // Transform analysis results to frontend format
      const primaryRulesRaw: RuleMatch[] = await Promise.all(
        analysis.aggregated_results.map(async result => {
          const rule = result.record as any;

          // Use consolidated rule sentiment data (now stored directly in rules.json)
          let outcomeSentiment = null;
          if (rule.sentiment && rule.severity !== undefined) {
            outcomeSentiment = {
              sentiment: rule.sentiment,
              severity: rule.severity,
              confidence: 1.0, // Consolidated data is high confidence
            };
          }

          return {
            id: rule.id,
            condition: rule.if,
            outcome: rule.then,
            collection: rule.collection,
            citation: rule.citation_key,
            confidence: result.boosted_score ?? result.similarity ?? 0, // RRF aggregated score with context boosting (fallback to similarity for tests)
            sourceSentence: result.source_sentence,
            matchedSegments: result.matched_segments, // All segments that matched (for RRF display)
            outcomeSentiment,
            tokenContributions: result.tokenContributions, // Per-rule SHAP attribution
          };
        })
      );

      // Normalize scores relative to max (top result = 100%, others proportional to their actual score)
      const maxConfidence = Math.max(...primaryRulesRaw.map(r => r.confidence), 0.001);
      const primaryRules = primaryRulesRaw.map(rule => ({
        ...rule,
        confidence: rule.confidence / maxConfidence, // Score-based percentage
      }));

      // Transform entity results
      const symbolicMeaningsRaw: EntityMatch[] = entities.map(result => {
        const entity = result.record as any;
        return {
          id: entity.id,
          name: entity.name,
          summary: entity.summary,
          category: entity.category || 'unknown',
          primaryCategory: entity.primary_category,
          secondaryCategory: entity.secondary_category,
          confidence: result.similarity,
        };
      });

      // Normalize scores relative to max
      const maxEntityScore = Math.max(...symbolicMeaningsRaw.map(e => e.confidence), 0.001);
      const symbolicMeanings = symbolicMeaningsRaw.map(entity => ({
        ...entity,
        confidence: entity.confidence / maxEntityScore,
      }));

      const processingTime = Math.round(performance.now() - startTime);

      // Transform aggregate sentiment analysis results
      let sentimentAnalysis: AnalysisResults['sentimentAnalysis'];
      if (
        analysis.aggregate_sentiment &&
        analysis.aggregate_sentiment.analyzedOutcomes.length > 0
      ) {
        const goodCount = analysis.aggregate_sentiment.analyzedOutcomes.filter(
          o => o.sentiment === 'good'
        ).length;
        const badCount = analysis.aggregate_sentiment.analyzedOutcomes.filter(
          o => o.sentiment === 'bad'
        ).length;
        const neutralCount = analysis.aggregate_sentiment.analyzedOutcomes.filter(
          o => o.sentiment === 'neutral'
        ).length;

        sentimentAnalysis = {
          aggregateResult: analysis.aggregate_sentiment,
          summary: {
            dominantSentiment: analysis.aggregate_sentiment.sentiment,
            aggregateSeverity: analysis.aggregate_sentiment.severity,
            ritualSeverity: analysis.interpretation_summary.ritual_severity,
            confidenceScore: analysis.aggregate_sentiment.confidence,
            rawScore: analysis.aggregate_sentiment.raw_score,
            analyzedOutcomes: analysis.aggregate_sentiment.analyzedOutcomes.length,
            goodCount,
            badCount,
            neutralCount,
          },
        };
      }

      const results: AnalysisResults = {
        originalDream: {
          text: formData.dreamText,
          context: formData.context,
          truncated: analysis.truncated,
          truncationWarning: analysis.truncation_warning,
        },
        primaryRules,
        symbolicMeanings,
        sentimentAnalysis,
        ritualRecommendations: analysis.ritual_recommendations ? (() => {
          // Normalize ritual scores relative to max
          const rituals = analysis.ritual_recommendations;
          const maxRitualScore = Math.max(...rituals.map(r => r.combined_score), 0.001);
          return rituals.map(ritual => ({
            ...ritual,
            combined_score: ritual.combined_score / maxRitualScore,
          }));
        })() : undefined,
        processingTime,
        timestamp: Date.now(),
      };

      // LLM filter was broken and has been removed - using pure semantic search results

      console.log(`✅ Dream analysis complete (${processingTime}ms)`);
      return results;
    } catch (error) {
      console.error('❌ Dream analysis failed:', error);
      throw error;
    } finally {
      setIsAnalyzing(false);
    }
  }, []);

  /**
   * Get segmented encyclopedia matches using max-similarity-across-segments.
   * Encyclopedia entries are broad concepts that naturally match multiple segments.
   * Unlike rules (which need RRF for narrow condition matching), encyclopedia entries
   * should use the best segment match score — the 70% quality gate in RRF over-filters them.
   */
  const getSegmentedEncyclopediaMatches = async (
    dreamText: string,
    embeddingService: EmbeddingService
  ) => {
    const { DreamSentencizer } = await import('../services/dream-sentencizer');

    console.log('🔍 Searching encyclopedia with segmented approach...');

    const dreamSegments = DreamSentencizer.sentencize(dreamText, {
      minSegmentLength: 15,
      maxSegmentLength: 300,
      preserveContext: true,
    });

    if (dreamSegments.length === 0) return [];

    // Query each segment for encyclopedia entries
    const allSegmentResults = [];
    for (const segment of dreamSegments) {
      console.log(
        `   🔍 Encyclopedia segment ${segment.index + 1}: "${segment.text.slice(0, 40)}..."`
      );

      const matches = await embeddingService.search({
        text: segment.text,
        filters: { types: ['encyclopedia'] },
        max_results: 6,
      });

      console.log(`      Found ${matches.length} encyclopedia matches`);
      allSegmentResults.push({ segment, results: matches });
    }

    // Max-similarity merge: for each entry, keep only the best segment match
    const bestScores = new Map<string, { result: (typeof allSegmentResults)[0]['results'][0]; score: number }>();

    for (const { results } of allSegmentResults) {
      for (const result of results) {
        const entryId = (result.record as any).id;
        const existing = bestScores.get(entryId);

        if (!existing || result.similarity > existing.score) {
          bestScores.set(entryId, { result, score: result.similarity });
        }
      }
    }

    // Sort by best segment score
    const sortedEntries = Array.from(bestScores.values())
      .sort((a, b) => b.score - a.score);

    // Category-balanced selection: cap narrative/literature categories so concrete
    // symbols aren't crowded out by broad literary texts
    const CATEGORY_CAPS: Record<string, number> = {
      ancient_literature: 1,
      dream_narratives: 1,
    };
    const categoryCounts: Record<string, number> = {};
    const balancedEntries: typeof sortedEntries = [];

    for (const entry of sortedEntries) {
      if (balancedEntries.length >= 8) break;
      const category = (entry.result.record as any).category || '';
      const cap = CATEGORY_CAPS[category];
      if (cap !== undefined) {
        const count = categoryCounts[category] || 0;
        if (count >= cap) continue; // Skip — this category is full
        categoryCounts[category] = count + 1;
      }
      balancedEntries.push(entry);
    }

    const totalSegmentResults = allSegmentResults.flatMap(sr => sr.results).length;
    console.log(
      `   Encyclopedia: ${totalSegmentResults} segment results → ${balancedEntries.length} unique entries (max-similarity, category-balanced)`
    );

    return balancedEntries.map(entry => ({
      ...entry.result,
      similarity: entry.score,
    }));
  };

  return {
    analyzeDream,
    isAnalyzing,
  };
};
